import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  Store,
  Users,
  Truck,
  FolderTree,
  Image,
  Tag,
  LogOut,
  MapPin,
  Shield,
  Bell,
  UserCog,
  Compass,
  Receipt,
  Handshake,
  Star,
  Gift,
  BadgePercent,
  Settings as SettingsIcon,
  Smartphone,
  Clapperboard,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';

// isOpen / onClose control the mobile/tablet slide-out drawer state (owned by
// Layout). On lg+ screens the sidebar is always visible as a static column
// regardless of isOpen — see the `lg:translate-x-0 lg:static` classes below.
const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin, can } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allMenuItems = [
    { name: 'Dashboard', path: '/dashboard', resource: 'dashboard', icon: Home },
    { name: 'Admins', path: '/admins', resource: 'admins', icon: UserCog },
    { name: 'Roles & Permissions', path: '/roles', resource: 'roles', icon: Shield },
    { name: 'Zones', path: '/zones', resource: 'zones', icon: FolderTree },
    { name: 'Categories', path: '/categories', resource: 'categories', icon: FolderTree },
    { name: 'Banners', path: '/banners', resource: 'banners', icon: Image },
    { name: 'Explore More', path: '/explore-more', resource: 'explore_more', icon: Compass },
    { name: 'Restaurants', path: '/restaurants', resource: 'restaurants', icon: Store },
    { name: 'Commission Management', path: '/commission-management', resource: 'commissions', icon: BadgePercent },
    { name: 'Orders', path: '/orders', resource: 'orders', icon: Users },
    { name: 'Transactions', path: '/transactions', resource: 'transactions', icon: Receipt },
    { name: 'Delivery Agents', path: '/delivery-agents', resource: 'delivery_agents', icon: Truck },
    { name: 'Agent Locations', path: '/agent-locations', resource: 'agent_locations', icon: MapPin },
    { name: 'Offers', path: '/offers', resource: 'offers', icon: Tag },
    { name: 'Affiliate System', path: '/affiliate-system', resource: 'affiliate', icon: Handshake },
    { name: 'Refer & Earn', path: '/refer-earn', resource: 'referrals', icon: Gift },
    { name: 'Reviews & Ratings', path: '/reviews', resource: 'reviews', icon: Star },
    { name: 'Users', path: '/users', resource: 'users', icon: Users },
    { name: 'Notifications', path: '/notifications', resource: 'notifications', icon: Bell },
    { name: 'App Icon', path: '/app-icon', resource: 'app_icons', icon: Smartphone },
    { name: 'Launch Screen', path: '/launch-screen', resource: 'launch_screen', icon: Clapperboard },
    { name: 'Settings', path: '/settings', resource: 'settings', icon: SettingsIcon },
  ];

  // Only show pages the admin's role can view (Super Admin sees everything).
  const menuItems = allMenuItems.filter((item) => can(item.resource, 'view'));

  return (
    <>
      {/* Backdrop — mobile/tablet only, shown while the drawer is open */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] h-screen bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-200 ease-in-out lg:static lg:z-auto lg:w-60 lg:max-w-none lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ fontFamily: 'Public Sans, sans-serif' }}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2
            className="text-xl font-bold"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#C2410C' }}
          >
            FoodKAdmin
          </h2>

          {/* Close button — mobile/tablet only */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 lg:hidden"
            data-testid="sidebar-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* MENU (SCROLLABLE) */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`sidebar-${item.name
                  .toLowerCase()
                  .replace(/ /g, '-')}`}
                className={`flex items-center text-sm gap-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-orange-50 text-orange-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* FOOTER (FIXED) */}
        <div className="p-2 border-t border-gray-200">
          <div className="mb-2 px-4 py-2">
            <p className="text-sm font-medium text-gray-900">
              {admin?.name || 'Admin'}
            </p>
            <p className="text-xs text-gray-500">
              {admin?.email || ''}
            </p>
          </div>

          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
